import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/dev/prototypes/clearvoid-v1-components")(
	{
		validateSearch: (search: Record<string, unknown>) => ({
			focus: typeof search.focus === "string" ? search.focus : undefined,
		}),
		component: FindingsV1Components,
	},
);

const FONT_HREF =
	"https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Inter:wght@200;300;400&display=swap";

const ICONS_HREF =
	"https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap";

// --- Sample findings for each type ---

const SAMPLE = {
	claim: {
		type: "claim" as const,
		label: "Claim",
		icon: "lightbulb",
		content:
			"Specific knowledge is knowledge that you cannot be trained for. If society can train you for it, it can train someone else and replace you.",
	},
	quote: {
		type: "quote" as const,
		label: "Quote",
		icon: "auto_awesome",
		content:
			"\u201CWealth is assets that earn while you sleep. Money is how we transfer time and wealth.\u201D",
	},
	prediction: {
		type: "prediction" as const,
		label: "Prediction",
		icon: "trending_up",
		content:
			"Code and media are permissionless leverage. In the future, everyone will have an army of robots working for them.",
	},
	opinion: {
		type: "opinion" as const,
		label: "Opinion",
		icon: "chat_bubble",
		content:
			"The barriers to creating media are basically gone. You and a microphone can reach millions of people.",
	},
};

type SampleKey = keyof typeof SAMPLE;
const SAMPLE_KEYS: SampleKey[] = ["claim", "quote", "prediction", "opinion"];

const TYPE_TEXT: Record<SampleKey, string> = {
	claim: "text-claim",
	quote: "text-quote",
	prediction: "text-prediction",
	opinion: "text-opinion",
};

const TYPE_BORDER_L: Record<SampleKey, string> = {
	claim: "border-l-claim",
	quote: "border-l-quote",
	prediction: "border-l-prediction",
	opinion: "border-l-opinion",
};

// --- Shared helpers ---

function ShowcaseLabel({
	name,
	focus,
}: {
	name: string;
	focus: string | undefined;
}) {
	const isActive = focus === name;
	return (
		<Link
			to="/dev/prototypes/clearvoid-v1-components"
			search={isActive ? { focus: undefined } : { focus: name }}
			className={`text-[11px] font-medium tracking-[0.2em] uppercase transition-colors ${
				isActive ? "text-gold" : "text-ivory/50 hover:text-ivory/80"
			}`}
		>
			{name}
		</Link>
	);
}

function ShowcaseItem({
	name,
	focus,
	children,
}: {
	name: string;
	focus: string | undefined;
	children: React.ReactNode;
}) {
	if (focus && focus !== name) return null;

	return (
		<div>
			<ShowcaseLabel name={name} focus={focus} />
			<div className="mt-4 max-w-2xl">{children}</div>
		</div>
	);
}

// --- Card Variants ---

/** A. Original — type label on its own row */
function CardOriginal({ k }: { k: SampleKey }) {
	const s = SAMPLE[k];
	const color = TYPE_TEXT[k];
	const isQuote = s.type === "quote";
	return (
		<article className="border-ivory/8 bg-dark-accent hover:border-ivory/15 rounded-lg border p-7 transition-colors duration-300">
			<div className="mb-4">
				<span
					className={`${color} text-xs font-medium tracking-wider uppercase`}
				>
					{s.label}
				</span>
			</div>
			{isQuote ? (
				<blockquote className="font-display text-ivory text-lg leading-relaxed italic">
					{s.content}
				</blockquote>
			) : (
				<p className="text-ivory text-[17px] leading-relaxed">
					{s.content}
				</p>
			)}
		</article>
	);
}

/** B. Inline prefix — type label flows into the first line */
function CardInlinePrefix({ k }: { k: SampleKey }) {
	const s = SAMPLE[k];
	const color = TYPE_TEXT[k];
	const isQuote = s.type === "quote";
	return (
		<article className="border-ivory/8 bg-dark-accent hover:border-ivory/15 rounded-lg border p-7 transition-colors duration-300">
			{isQuote ? (
				<blockquote className="font-display text-ivory text-lg leading-relaxed italic">
					<span
						className={`${color} font-sans text-xs font-medium tracking-wider uppercase not-italic`}
					>
						{s.label}
					</span>{" "}
					{s.content}
				</blockquote>
			) : (
				<p className="text-ivory text-[17px] leading-relaxed">
					<span
						className={`${color} text-xs font-medium tracking-wider uppercase`}
					>
						{s.label}
					</span>{" "}
					{s.content}
				</p>
			)}
		</article>
	);
}

/** C. Colored left border — no label, border color is the only type signal */
function CardLeftBorder({ k }: { k: SampleKey }) {
	const s = SAMPLE[k];
	const borderL = TYPE_BORDER_L[k];
	const isQuote = s.type === "quote";
	return (
		<article
			className={`border-ivory/8 bg-dark-accent hover:border-ivory/15 rounded-lg border border-l-[3px] ${borderL} p-7 transition-colors duration-300`}
		>
			{isQuote ? (
				<blockquote className="font-display text-ivory text-lg leading-relaxed italic">
					{s.content}
				</blockquote>
			) : (
				<p className="text-ivory text-[17px] leading-relaxed">
					{s.content}
				</p>
			)}
		</article>
	);
}

/** D. Top-right badge — label floats absolutely, text flows full-width */
function CardTopRightBadge({ k }: { k: SampleKey }) {
	const s = SAMPLE[k];
	const color = TYPE_TEXT[k];
	const isQuote = s.type === "quote";
	return (
		<article className="border-ivory/8 bg-dark-accent hover:border-ivory/15 relative rounded-lg border p-7 transition-colors duration-300">
			<span
				className={`${color} absolute top-3 right-4 text-[10px] font-medium tracking-widest uppercase`}
			>
				{s.label}
			</span>
			{isQuote ? (
				<blockquote className="font-display text-ivory text-lg leading-relaxed italic">
					{s.content}
				</blockquote>
			) : (
				<p className="text-ivory text-[17px] leading-relaxed">
					{s.content}
				</p>
			)}
		</article>
	);
}

/** E. Bottom-right badge — label floats absolutely at bottom-right */
function CardBottomRightBadge({ k }: { k: SampleKey }) {
	const s = SAMPLE[k];
	const color = TYPE_TEXT[k];
	const isQuote = s.type === "quote";
	return (
		<article className="border-ivory/8 bg-dark-accent hover:border-ivory/15 relative rounded-lg border p-7 transition-colors duration-300">
			<span
				className={`${color} absolute right-4 bottom-3 text-[10px] font-medium tracking-widest uppercase`}
			>
				{s.label}
			</span>
			{isQuote ? (
				<blockquote className="font-display text-ivory text-lg leading-relaxed italic">
					{s.content}
				</blockquote>
			) : (
				<p className="text-ivory text-[17px] leading-relaxed">
					{s.content}
				</p>
			)}
		</article>
	);
}

/** F. Rotated outside label — type rotated 90deg on the left edge */
function CardRotatedOutside({ k }: { k: SampleKey }) {
	const s = SAMPLE[k];
	const color = TYPE_TEXT[k];
	const isQuote = s.type === "quote";
	return (
		<article className="border-ivory/8 bg-dark-accent hover:border-ivory/15 relative ml-8 rounded-lg border p-7 transition-colors duration-300">
			<span
				className={`${color} absolute top-1/2 left-0 origin-center -translate-x-[calc(50%+16px)] -translate-y-1/2 -rotate-90 text-[10px] font-medium tracking-widest uppercase`}
			>
				{s.label}
			</span>
			{isQuote ? (
				<blockquote className="font-display text-ivory text-lg leading-relaxed italic">
					{s.content}
				</blockquote>
			) : (
				<p className="text-ivory text-[17px] leading-relaxed">
					{s.content}
				</p>
			)}
		</article>
	);
}

/** G. Border-cut pill — label sits on top border with bg to cut through it */
function CardBorderPill({ k }: { k: SampleKey }) {
	const s = SAMPLE[k];
	const color = TYPE_TEXT[k];
	const isQuote = s.type === "quote";
	return (
		<article className="border-ivory/8 bg-dark-accent hover:border-ivory/15 relative mt-3 rounded-lg border p-7 transition-colors duration-300">
			<span
				className={`${color} bg-dark-accent absolute -top-3 left-5 px-3 py-0.5 text-[10px] font-medium tracking-widest uppercase`}
			>
				{s.label}
			</span>
			{isQuote ? (
				<blockquote className="font-display text-ivory text-lg leading-relaxed italic">
					{s.content}
				</blockquote>
			) : (
				<p className="text-ivory text-[17px] leading-relaxed">
					{s.content}
				</p>
			)}
		</article>
	);
}

/** H. Border-cut pill (copy of G — edit freely) */
function CardBorderPillAlt({ k }: { k: SampleKey }) {
	const s = SAMPLE[k];
	const color = TYPE_TEXT[k];
	const isQuote = s.type === "quote";
	return (
		<article className="border-ivory/8 group bg-dark-accent hover:border-ivory/15 relative mt-3 rounded-lg border p-7 transition-colors duration-300">
			<span
				className={`${color} bg-dark-accent group-hover:border-ivory/20 border-t-ivory/15 absolute -top-3 left-5 rounded-sm border-t px-3 py-1 text-[10px] font-medium tracking-widest uppercase transition-colors duration-300 select-none`}
			>
				{s.label}
			</span>
			{isQuote ? (
				<blockquote className="font-display text-ivory text-lg leading-relaxed italic">
					{s.content}
				</blockquote>
			) : (
				<p className="text-ivory text-[17px] leading-relaxed">
					{s.content}
				</p>
			)}
		</article>
	);
}

// --- Page ---

function FindingsV1Components() {
	const { focus } = Route.useSearch();

	useEffect(() => {
		const fonts = document.createElement("link");
		fonts.rel = "stylesheet";
		fonts.href = FONT_HREF;
		document.head.appendChild(fonts);

		const icons = document.createElement("link");
		icons.rel = "stylesheet";
		icons.href = ICONS_HREF;
		document.head.appendChild(icons);

		return () => {
			document.head.removeChild(fonts);
			document.head.removeChild(icons);
		};
	}, []);

	return (
		<div className="bg-warm-gray text-ivory/90 selection:bg-gold selection:text-warm-gray min-h-screen font-sans antialiased">
			<div className="mx-auto max-w-[1400px] px-6 py-12 lg:px-12">
				<div className="mb-8 flex items-center justify-between">
					<h1 className="font-display text-2xl">
						Findings v1 — Card Variants
					</h1>
					{focus && (
						<Link
							to="/dev/prototypes/clearvoid-v1-components"
							search={{ focus: undefined }}
							className="text-gold text-[11px] font-medium tracking-[0.2em] uppercase"
						>
							Show All
						</Link>
					)}
				</div>

				<div className="space-y-16">
					<ShowcaseItem name="A — Original" focus={focus}>
						<div className="space-y-4">
							{SAMPLE_KEYS.map((k) => (
								<CardOriginal key={k} k={k} />
							))}
						</div>
					</ShowcaseItem>

					<ShowcaseItem name="B — Inline prefix" focus={focus}>
						<div className="space-y-4">
							{SAMPLE_KEYS.map((k) => (
								<CardInlinePrefix key={k} k={k} />
							))}
						</div>
					</ShowcaseItem>

					<ShowcaseItem name="C — Left border" focus={focus}>
						<div className="space-y-4">
							{SAMPLE_KEYS.map((k) => (
								<CardLeftBorder key={k} k={k} />
							))}
						</div>
					</ShowcaseItem>

					<ShowcaseItem name="D — Top-right badge" focus={focus}>
						<div className="space-y-4">
							{SAMPLE_KEYS.map((k) => (
								<CardTopRightBadge key={k} k={k} />
							))}
						</div>
					</ShowcaseItem>

					<ShowcaseItem name="E — Bottom-right badge" focus={focus}>
						<div className="space-y-4">
							{SAMPLE_KEYS.map((k) => (
								<CardBottomRightBadge key={k} k={k} />
							))}
						</div>
					</ShowcaseItem>

					<ShowcaseItem name="F — Rotated outside" focus={focus}>
						<div className="space-y-4">
							{SAMPLE_KEYS.map((k) => (
								<CardRotatedOutside key={k} k={k} />
							))}
						</div>
					</ShowcaseItem>

					<ShowcaseItem name="G — Border-cut pill" focus={focus}>
						<div className="space-y-6">
							{SAMPLE_KEYS.map((k) => (
								<CardBorderPill key={k} k={k} />
							))}
						</div>
					</ShowcaseItem>

					<ShowcaseItem name="H — Border-cut pill alt" focus={focus}>
						<div className="space-y-6">
							{SAMPLE_KEYS.map((k) => (
								<CardBorderPillAlt key={k} k={k} />
							))}
						</div>
					</ShowcaseItem>
				</div>
			</div>
		</div>
	);
}
